import React, { useState } from 'react';
import { motion, AnimatePresence } from "framer-motion";
import { 
  X, Book, FileText, Globe, HelpCircle, Map, MapPin, Layers, BookOpen, Compass, GraduationCap, ChevronDown, ChevronUp
} from 'lucide-react';

const baseImg = "images/tutorial/Navegación pagina Ordenamientos Territoriales.cleaned (1)_Página_";

const secciones = [
  {
    titulo: "Página principal de Ordenamiento Territorial",
    icono: Globe,
    descripcion: "Consulta recursos informativos que apoyan trámites y procesos de compatibilidad de usos del suelo.",
    enlace: "https://ordenamientoterritorial.jalisco.gob.mx/",
    detalles: [
      "Acceso directo a fichas informativas",
      "Trámites para proyectos de desarrollo",
      "Opinión técnica: requisitos y beneficios"
    ],
    imagenes: ["01", "02"]
  },
  {
    titulo: "Fichas Informativas",
    icono: FileText,
    descripcion: "Conoce los usos más solicitados y qué trámites necesitas para tu proyecto.",
    detalles: [
      "Aspectos clave para la realización de proyectos",
      "Procedimiento aplicable según el tipo de desarrollo"
    ],
    imagenes: ["03", "04"]
  },
  {
    titulo: "Regiones con Ordenamiento Ecológico",
    icono: MapPin,
    descripcion: "Explora regiones, municipios y documentos relacionados con su expedición.",
    detalles: [
      "Regiones como Río Zula, Sierra Occidental, Costa Sur, Lagunas, Cuenca Baja del Río Verde",
      "Acceso a bitácoras ambientales y PDFs"
    ],
    imagenes: ["05", "06"]
  },
  {
    titulo: "Preguntas Frecuentes",
    icono: HelpCircle,
    descripcion: "Respuestas sobre aspectos jurídicos, bitácoras ambientales y programas de ordenamiento.",
    detalles: [
      "Navegación por cada bitácora ambiental",
      "Descarga de archivos SHP, KMZ, GPKG"
    ],
    imagenes: ["07", "08"]
  },
  {
    titulo: "Fichas UGAs y Criterios de Regulación Ecológica (CRE)",
    icono: BookOpen,
    descripcion: "Lineamientos, estrategias y criterios aplicables a cada Unidad de Gestión Ambiental.",
    detalles: [
      "Lista completa de criterios por sector",
      "Lineamientos ecológicos por UGA"
    ],
    imagenes: ["09", "10"]
  },
  {
    titulo: "Visor de Ordenamiento por Región",
    icono: Map,
    descripcion: "Identifica política ambiental y UGA aplicable a tu predio mediante el mapa interactivo.",
    detalles: [
      "Consulta por localidad",
      "Activa/desactiva capas del mapa",
      "Dibuja polígonos y calcula superficies",
      "Buscador de localidades"
    ],
    imagenes: ["11", "12"]
  },
  {
    titulo: "Fases del Ordenamiento",
    icono: Layers,
    descripcion: "Consulta acuerdos, actas de aprobación, consulta pública y boletas registrales.",
    detalles: [
      "Historial completo de cada ordenamiento"
    ],
    imagenes: ["13", "14"]
  },
  {
    titulo: "Ordenamiento Ecológico Territorial del Estado de Jalisco",
    icono: Book,
    descripcion: "Aplicable a municipios sin ordenamiento regional. Consulta fechas de publicación y actualización.",
    detalles: [
      "Descargas vectoriales: POETEJ, POETR, POEL",
      "Nota: Costa Alegre fue derogado por Costalegre (Cabo Corrientes, Tomatlán, La Huerta, Cihuatlán)"
    ],
    imagenes: ["15", "16", "17", "18"]
  }
];

const TutorialNavegacion = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [seccionActiva, setSeccionActiva] = useState(null);

  const toggleSeccion = (index) => {
    setSeccionActiva(seccionActiva === index ? null : index);
  };

  return (
    <div className="fixed bottom-6 left-6 z-[9999] font-sans">
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="mb-4 w-[90vw] max-w-4xl max-h-[85vh] bg-white border border-slate-200 rounded-3xl shadow-[0_20px_50px_rgba(0,0,0,0.15)] flex flex-col overflow-hidden"
          >
            <div className="bg-gradient-to-r from-blue-600 to-teal-600 p-5 flex justify-between items-center">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/10 rounded-xl">
                  <Compass className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-bold text-sm leading-tight">Guía de Navegación</h3>
                  <span className="text-white/70 text-[10px] font-bold uppercase tracking-wider">
                    Tutorial del sitio
                  </span>
                </div>
              </div>
              <button 
                onClick={() => setIsOpen(false)} 
                className="text-white/70 hover:text-white transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="flex-grow overflow-y-auto p-5 bg-slate-50 space-y-4">
              <p className="text-sm text-slate-600 mb-4">
                Sitio web:{' '}
                <a href="https://ordenamientoterritorial.jalisco.gob.mx/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 font-medium underline">
                  https://ordenamientoterritorial.jalisco.gob.mx/
                </a>
              </p>
              
              {secciones.map((seccion, idx) => {
                const Icono = seccion.icono;
                return (
                  <div key={idx} className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
                    <button
                      onClick={() => toggleSeccion(idx)}
                      className="w-full p-4 flex items-center justify-between text-left hover:bg-slate-50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-gradient-to-br from-blue-500 to-teal-500 rounded-lg flex-shrink-0">
                          <Icono className="w-4 h-4 text-white" />
                        </div>
                        <span className="text-sm font-semibold text-slate-800">{seccion.titulo}</span>
                      </div>
                      {seccionActiva === idx ? (
                        <ChevronUp className="w-4 h-4 text-slate-400 flex-shrink-0" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" />
                      )}
                    </button>
                    <AnimatePresence>
                      {seccionActiva === idx && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="overflow-hidden"
                        >
                          <div className="px-4 pb-4 pt-0 border-t border-slate-100">
                            <p className="text-sm text-slate-600 mt-3">{seccion.descripcion}</p>
                            {seccion.enlace && (
                              <p className="mt-2">
                                <a href={seccion.enlace} target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:text-teal-800 text-sm font-medium underline">
                                  {seccion.enlace}
                                </a>
                              </p>
                            )}
                            {seccion.imagenes && seccion.imagenes.length > 0 && (
                              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {seccion.imagenes.map((pagina, i) => (
                                  <a
                                    key={i}
                                    href={`${baseImg}${pagina}.png`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="block group"
                                  >
                                    <img
                                      src={`${baseImg}${pagina}.png`}
                                      alt={`${seccion.titulo} - Página ${pagina}`}
                                      className="w-full rounded-lg border border-slate-200 shadow-sm group-hover:shadow-md transition-shadow"
                                      loading="lazy"
                                    />
                                  </a>
                                ))}
                              </div>
                            )}
                            <ul className="mt-3 space-y-1">
                              {seccion.detalles.map((detalle, i) => (
                                <li key={i} className="text-sm text-slate-500 flex items-start gap-2">
                                  <span className="text-teal-500 mt-0.5">•</span>
                                  {detalle}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}

              <div className="mt-4 p-4 bg-amber-50 rounded-xl border border-amber-200 border-l-4 border-l-amber-400">
                <p className="text-sm text-amber-800">
                  <strong>Nota importante:</strong> Para municipios sin ordenamiento regional, aplica el Ordenamiento Estatal.
                  El ordenamiento de Costa Alegre fue derogado por <strong>Costalegre</strong> (Cabo Corrientes, Tomatlán, La Huerta, Cihuatlán).
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsOpen(!isOpen)}
        className={`h-14 w-14 rounded-full flex items-center justify-center shadow-[0_10px_30px_rgba(0,0,0,0.2)] transition-all duration-300 ${
          isOpen ? 'bg-slate-800 text-white' : 'bg-gradient-to-r from-blue-600 to-teal-600 text-white'
        }`}
      >
        {isOpen ? <X size={24} /> : (
          <div className="relative">
            <GraduationCap size={24} />
            <div className="absolute -top-1 -right-1 w-3 h-3 bg-amber-400 rounded-full border-2 border-white animate-ping" />
          </div>
        )}
      </motion.button>
    </div>
  );
};

export default TutorialNavegacion;
